import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';
import * as mobxReact from 'mobx-react';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { Paging } from '../../../global/_paging/paging-container';
import { IHubAttendeeListItemDTO, THubAttendeesSortKeys } from '../../../../api/event-hubs.dao';
import { HubAttendeesListItem } from './_hub-attendees-list-item/hub-attendees-list-item';
import { EventHubAttendeesViewModel } from './event-hub-attendees.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import MultiSelectionDropdown from '../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { SelectLeftColumnIcon } from '../../../global/icons/icons';
import './event-hub-attendees.scss';
import {
    HUB_ATTENDEES_COLUMN_ACCESS_CODE_TEXT,
    HUB_ATTENDEES_COLUMN_ASSISTANT_EMAIL_TEXT,
    HUB_ATTENDEES_COLUMN_EMAIL_TEXT,
    HUB_ATTENDEES_COLUMN_EXTERNAL_ATTENDEE_ID_TEXT,
    HUB_ATTENDEES_COLUMN_NAME_TEXT,
    HUB_ATTENDEES_COLUMN_ORGANIZATION_TEXT,
    HUB_ATTENDEES_COLUMN_ORGANIZATION_TYPE_TEXT,
    HUB_ATTENDEES_COLUMN_PHONE_NUMBER_TEXT,
    HUB_ATTENDEES_COLUMN_REGISTRATION_DATE_TEXT,
    HUB_ATTENDEES_COLUMN_SALUTATION_TEXT,
    HUB_ATTENDEES_COLUMN_STATUS_TEXT,
    HUB_ATTENDEES_COLUMN_TITLE_TEXT,
    NO_HUB_ATTENDEES_MESSAGE_TEXT,
} from '../../event-hubs.text';
import {
    MANDATORY_HUB_ATTENDEE_COLUMN_ID,
    THubAttendeeColumnId,
    toCustomFieldColumnId,
} from './hub-attendee-columns';

const ID_FOR_COLUMN_SELECTOR = uuid();

type EventHubAttendeesPresentationProps = {
    vm: EventHubAttendeesViewModel;
};

export const EventHubAttendeesPresentation = mobxReact.observer(({ vm }: EventHubAttendeesPresentationProps): JSX.Element => {
    const intl = useIntl();

    const isLoading = vm.isFetchingAttendees();
    const isEmpty = !vm.getAttendees()?.length && !isLoading;
    const isEditable = hasPermission(Permission.EDIT_HUB_ATTENDEES);
    const canEditHubOrganization = hasPermission(Permission.EDIT_HUB_ORGANIZATION);
    const [isColumnSelectorTouched, setColumnSelectorTouched] = useState<boolean>(false);

    const visibleColumns = vm.getVisibleColumnIds();
    const customFields = vm.getCustomFields();

    const fixedAttendeeColumns: {
        id: THubAttendeeColumnId;
        title: string;
        sortField?: THubAttendeesSortKeys;
    }[] = useMemo(() => [
        {
            id: 'name',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_NAME_TEXT),
            sortField: 'lastName',
        },
        {
            id: 'email',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_EMAIL_TEXT),
            sortField: 'email',
        },
        {
            id: 'organization',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_ORGANIZATION_TEXT),
            sortField: 'organization.name',
        },
        {
            id: 'organizationType',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_ORGANIZATION_TYPE_TEXT),
            sortField: 'organization.organizationType',
        },
        {
            id: 'title',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_TITLE_TEXT),
            sortField: 'title',
        },
        {
            id: 'phoneNumber',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_PHONE_NUMBER_TEXT),
            sortField: 'phoneNumber',
        },
        {
            id: 'salutation',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_SALUTATION_TEXT),
            sortField: 'salutation',
        },
        {
            id: 'assistantEmail',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_ASSISTANT_EMAIL_TEXT),
            sortField: 'assistantEmail',
        },
        {
            id: 'accessCode',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_ACCESS_CODE_TEXT),
            sortField: 'accessCode',
        },
        {
            id: 'externalAttendeeId',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_EXTERNAL_ATTENDEE_ID_TEXT),
            sortField: 'externalAttendeeId',
        },
        {
            id: 'registrationDate',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_REGISTRATION_DATE_TEXT),
            sortField: 'registrationDate',
        },
        {
            id: 'registrationStatus',
            title: intl.formatMessage(HUB_ATTENDEES_COLUMN_STATUS_TEXT),
            sortField: 'registrationStatus',
        },
    ], [intl]);
    // Custom field columns go last, are labelled with the field label and are not sortable.
    const customFieldColumns: typeof fixedAttendeeColumns = customFields.map(field => ({
        id: toCustomFieldColumnId(field.customFieldId),
        title: field.label,
    }));
    const attendeeColumns = [...fixedAttendeeColumns, ...customFieldColumns];
    const selectableAttendeeColumns = attendeeColumns.filter(column => column.id !== MANDATORY_HUB_ATTENDEE_COLUMN_ID);

    const resetColumnSelector = (): void => {
        vm.resetColumns();
        setColumnSelectorTouched(false);
    };

    const isResetButtonDisabled = isColumnSelectorTouched || !vm.isDefaultColumnSelection();
    const selectedColumns = visibleColumns
        .map(columnId => attendeeColumns.find(column => column.id === columnId))
        .filter((column): column is (typeof attendeeColumns)[number] => column != null);

    const theads: ITheadSort<THubAttendeesSortKeys>[] = [
        ...selectedColumns.map(column => ({
            title: column.title,
            sortField: column.sortField,
        })),
        {
            title: (
                <MultiSelectionDropdown
                    entityName=''
                    noItemsPlaceholder={<></>}
                    onApply={columns => {
                        vm.applyColumnSelection(columns);
                        setColumnSelectorTouched(false);
                    }}
                    withSearch={false}
                    withIcon={<SelectLeftColumnIcon />}
                    onFetchItems={() => Promise.resolve(selectableAttendeeColumns.map(column => ({
                        id: column.id,
                        label: column.title,
                    })))}
                    savedItems={visibleColumns.filter(column => column !== MANDATORY_HUB_ATTENDEE_COLUMN_ID)}
                    customReset={resetColumnSelector}
                    isResetButtonDisabled={isResetButtonDisabled}
                    setResetButtonStatus={setColumnSelectorTouched}
                    withoutHighlight={true}
                    classNames='aligned-right'
                    customLeft={-180}
                    key={ID_FOR_COLUMN_SELECTOR}
                    isSetInitialItems={true}
                />
            ),
            width: 72,
        },
        { title: '', width: 80 },
    ];

    return (
        <div className='event-hub-attendees-tab'>
            <section className='events-list fixed-list-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: theads,
                            sortRows: vm.setSortParams,
                            sortOrder: vm.getSortOrder(),
                            sortField: vm.getSortField(),
                        }}
                        isDisabled={isEmpty}
                    >
                        {(!isLoading && vm.getAttendees())
                            ? vm.getAttendees().map((item: IHubAttendeeListItemDTO) => (
                                <HubAttendeesListItem
                                    attendee={item}
                                    key={item.attendeeId}
                                    onEdit={() => vm.editAttendee(item)}
                                    isEditable={isEditable}
                                    canEditHubOrganization={canEditHubOrganization}
                                    onEditOrganization={() => { void vm.editHubOrganizationForAttendee(item); }}
                                    visibleColumnIds={visibleColumns}
                                />
                            ))
                            : <TableLoadingRow colSpan={theads.length} />
                        }
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            <FormattedMessage {...NO_HUB_ATTENDEES_MESSAGE_TEXT} />
                        </div>
                    }
                </div>
                {!isLoading && <Paging pagingVM={vm.getPagingVM()} />}
            </section>
        </div>
    );
});
